import * as z from 'zod';
import { EditorDocumentSchema } from './editor.ts';
import { CaptureKind, Id, ItemKind, LanguageCode, OperationKind, Role, Shortcut } from './model.ts';

/**
 * Command inputs shared by the HTTP API and MCP tools. The workspace and the
 * acting user/agent always come from authentication, never from these inputs.
 */

const IdempotencyKey = z
  .string()
  .min(8)
  .max(200)
  .describe('Optional retry key. Re-sending the same request with the same key returns the original result; a different request under the same key is rejected.');
const FolderName = z.string().trim().min(1).max(200);
const Title = z.string().max(500);
const TagName = z.string().trim().min(1).max(64);
const Sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'lowercase hex SHA-256');
const Markdown = z.string().max(1_000_000);

export const CreateFolderInput = z.object({
  name: FolderName,
  parentId: Id.nullable().optional().describe('Parent folder id; omit or null for a top-level folder.'),
  idempotencyKey: IdempotencyKey.optional(),
});
export const RenameFolderInput = z.object({ folderId: Id, name: FolderName });
export const MoveFolderInput = z.object({
  folderId: Id,
  parentId: Id.nullable().describe('New parent folder id, or null to move to the top level.'),
});
export const DeleteFolderInput = z.object({ folderId: Id });

export const ListItemsInput = z.object({
  folderId: Id.optional().describe('Only items directly in this folder.'),
  kind: ItemKind.optional(),
  capture: CaptureKind.optional().describe('Only recordings made as voice notes or meetings.'),
  view: z.enum(['active', 'trash']).default('active'),
  limit: z.number().int().min(1).max(200).default(50),
  cursor: z.string().max(500).optional().describe('nextCursor from the previous page.'),
});
export const ItemRefInput = z.object({ itemId: Id });
export const UpdateItemInput = z.object({
  itemId: Id,
  title: Title.optional(),
  description: z.string().max(2000).nullable().optional(),
  addTags: z.array(TagName).max(20).optional(),
  removeTags: z.array(TagName).max(20).optional(),
});
export const MoveItemInput = z.object({ itemId: Id, folderId: Id });

const NoteBody = {
  markdown: Markdown.optional().describe('Note body as Markdown.'),
  document: EditorDocumentSchema.optional().describe('Note body as editor JSON (tiptap_json_v1).'),
};

export const CreateNoteInput = z
  .object({
    folderId: Id.optional().describe('Destination folder; defaults to Inbox.'),
    title: Title.optional(),
    ...NoteBody,
    idempotencyKey: IdempotencyKey.optional(),
  })
  .refine((input) => !(input.markdown !== undefined && input.document !== undefined), {
    message: 'Provide markdown or document, not both.',
  });

export const EditNoteInput = z
  .object({
    itemId: Id,
    baseRevisionId: Id.describe('revisionId from your latest read; a stale value returns a conflict instead of overwriting newer text.'),
    ...NoteBody,
    idempotencyKey: IdempotencyKey.optional(),
  })
  .refine((input) => (input.markdown === undefined) !== (input.document === undefined), {
    message: 'Provide exactly one of markdown or document.',
  });

export const AppendToNoteInput = z.object({
  itemId: Id,
  baseRevisionId: Id.describe('revisionId from your latest read; a stale value returns a conflict.'),
  markdown: z.string().min(1).max(200_000),
  idempotencyKey: IdempotencyKey.optional(),
});

/** Replaces the body of a plain-text item (e.g. extracted text) with the same stale-save check as notes. */
export const EditTextInput = z.object({
  itemId: Id,
  baseRevisionId: Id.describe('revisionId from your latest read; a stale value returns a conflict.'),
  text: z.string().max(5_000_000),
  idempotencyKey: IdempotencyKey.optional(),
});

export const ReadTextInput = z.object({
  itemId: Id,
  offset: z.number().int().min(0).default(0),
  maxChars: z.number().int().min(1).max(200_000).default(50_000),
});

export const BeginUploadInput = z.object({
  folderId: Id.optional().describe('Destination folder; defaults to Inbox.'),
  filename: z.string().trim().min(1).max(500),
  mimeType: z.string().max(200).optional(),
  sizeBytes: z.number().int().min(0),
  sha256: Sha256.optional().describe('Expected SHA-256 of the bytes; completion fails if it does not match.'),
  capture: CaptureKind.optional().describe('For audio or video: voice_note also makes a cleaned transcript; meeting also makes meeting notes. Both are transcribed.'),
  title: z.string().trim().min(1).max(500).optional().describe('Title for the item; defaults to the filename (which downloads keep).'),
  idempotencyKey: IdempotencyKey.optional(),
});
export const CompleteUploadInput = z.object({ uploadId: Id });
export const UploadTextFileInput = z.object({
  folderId: Id.optional().describe('Destination folder; defaults to Inbox.'),
  filename: z.string().trim().min(1).max(500),
  text: z.string().max(1_000_000),
  mimeType: z.string().max(200).optional(),
  idempotencyKey: IdempotencyKey.optional(),
});

export const SearchInput = z.object({
  query: z.string().trim().min(1).max(500),
  kind: ItemKind.optional(),
  limit: z.number().int().min(1).max(50).default(20),
});

export const RelatedInput = z.object({
  itemId: Id,
  direction: z.enum(['sources', 'derivatives']),
  maxDepth: z.number().int().min(1).max(5).default(3),
});

export const TransformKind = z.enum(['summary', 'brief', 'report', 'notes', 'custom', 'clean_transcript', 'meeting_notes']);
export const TransformInput = z
  .object({
    sourceItemIds: z.array(Id).min(1).max(20).describe('Notes, files or text items to work from, most important first.'),
    kind: TransformKind.describe('summary, brief, report, notes (organized notes), or custom (follow the instructions).'),
    instructions: z.string().trim().min(1).max(4000).optional().describe('What to write. Required for custom; optional extra guidance otherwise.'),
    title: z.string().trim().min(1).max(200).optional().describe('Title for the new note. Default: one the AI proposes.'),
    folderId: Id.optional().describe("Where to put the new note. Default: the sources' folder if they share one, otherwise Inbox."),
    idempotencyKey: IdempotencyKey.optional(),
  })
  .refine((input) => input.kind !== 'custom' || Boolean(input.instructions), {
    message: 'A custom transformation needs instructions.',
    path: ['instructions'],
  });

export const OperationRefInput = z.object({ operationId: Id });
export const ListOperationsInput = z.object({ itemId: Id, kind: OperationKind.optional() });
export const ReprocessItemInput = z.object({
  itemId: Id,
  step: z.enum(['extract_text', 'enrich_metadata', 'file_item', 'transcribe']).describe(
    "extract_text reads a file's text again; transcribe turns a recording's speech into a transcript again; enrich_metadata regenerates AI details (title, description, tags) wherever a person has not set them; file_item asks AI to file an item that is in the Inbox into a folder.",
  ),
});
export const ListActivityInput = z.object({
  afterSeq: z.number().int().min(0).optional().describe('Return events after this sequence number (exclusive).'),
  limit: z.number().int().min(1).max(200).default(50),
});

export const InviteMemberInput = z.object({
  email: z.email().max(320).transform((email) => email.trim().toLowerCase()).describe('Email address of the person to invite. They join by opening the link while signed in with this address.'),
  role: Role.default('editor'),
});
export const InvitationRefInput = z.object({ invitationId: Id });
export const AcceptInvitationInput = z.object({ token: z.string().min(20).max(200) });

export const CreateShareInput = z.object({
  itemId: Id,
  revisionId: Id.optional().describe('The version to share (from list_revisions); default the current one. Later versions are not shown until the link is updated.'),
  expiresInDays: z.number().int().min(1).max(365).optional().describe('Optional: the link stops working after this many days.'),
});
export const ShareRefInput = z.object({ shareId: Id });
export const UpdateShareInput = z.object({
  shareId: Id,
  revisionId: Id.optional().describe('The version the link shows from now on; default the current one.'),
});
export const ListSharesInput = z.object({ itemId: Id.optional() });

/** Longest selected text a command can rewrite. */
export const MAX_COMMAND_SELECTION = 20_000;

export const CommandInput = z.object({
  text: z.string().trim().min(1).max(2000),
  context: z.object({
    /** The items the command is about: the open item or the selected ones. */
    itemIds: z.array(Id).max(50).default([]),
    /** The folder the person is looking at. */
    folderId: Id.optional(),
    /** Text selected in an open note (for "rewrite this"). */
    selection: z.object({ itemId: Id, text: z.string().min(1).max(MAX_COMMAND_SELECTION) }).optional(),
  }),
});

const FieldOrigin = z.enum(['unset', 'system', 'manual']);

/**
 * Everything to undo on one item, applied together only if the item is
 * unchanged since the command (its hidden state version matches). Each step
 * restores the earlier value and who set it (a person, AI, or nobody).
 */
export const UndoStep = z.object({
  itemId: Id,
  expectedStateVersion: z.number().int(),
  inverse: z.array(
    z.discriminatedUnion('type', [
      z.object({ type: z.literal('restore') }),
      z.object({ type: z.literal('trash') }),
      z.object({ type: z.literal('move'), folderId: Id, placementOrigin: z.enum(['default', 'explicit', 'system']) }),
      z.object({ type: z.literal('rename'), title: Title, titleOrigin: FieldOrigin }),
      z.object({ type: z.literal('describe'), description: z.string().max(5000).nullable(), descriptionOrigin: FieldOrigin }),
      z.object({
        type: z.literal('tag'),
        /** Each tag the command touched, as it was before (null: it did not exist). */
        tags: z
          .array(z.object({ tag: TagName, before: z.object({ origin: z.enum(['manual', 'system']), state: z.enum(['active', 'suppressed']) }).nullable() }))
          .max(100),
      }),
    ]),
  ),
});
export const UndoCommandInput = z.object({ steps: z.array(UndoStep).max(100), cancelOperationIds: z.array(Id).max(50).default([]) });

export const CreateSiteInput = z
  .object({
    sourceItemIds: z.array(Id).min(1).max(20).optional().describe('Notes, files or text items to make the Site from, most important first (AI writes the page).'),
    htmlFileItemId: Id.optional().describe('Instead: an uploaded HTML file to open as a Site as it is (no AI).'),
    instructions: z.string().trim().min(1).max(4000).optional().describe('What the page should be or do, e.g. "a quiz with a score".'),
    title: Title.optional(),
    folderId: Id.optional().describe('Destination folder; defaults to the sources\' folder or Inbox.'),
    idempotencyKey: IdempotencyKey.optional(),
  })
  .refine((input) => Boolean(input.sourceItemIds?.length) !== Boolean(input.htmlFileItemId), {
    message: 'Give either sourceItemIds (AI writes the page) or htmlFileItemId (an HTML file opened as a Site).',
  });
export const ReviseSiteInput = z.object({
  itemId: Id,
  instructions: z.string().trim().min(1).max(4000).describe('What to change; the new version is written from the current one and its sources.'),
  idempotencyKey: IdempotencyKey.optional(),
});
export const ReadSiteInput = z.object({
  itemId: Id,
  revisionId: Id.optional().describe('A version from list_revisions; default the current one.'),
  maxChars: z.number().int().min(0).max(2_000_000).default(200_000).describe('Most characters of HTML returned; 0 for the preview link only.'),
});
export const ListRevisionsInput = z.object({ itemId: Id, limit: z.number().int().min(1).max(200).default(50) });

/** Where a new comment points; the server records the exact words around a text range itself. */
export const AnchorInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('item') }),
  z.object({ kind: z.literal('text'), start: z.number().int().min(0), end: z.number().int().min(1) }),
  z.object({ kind: z.literal('time'), startMs: z.number().int().min(0), endMs: z.number().int().min(0).optional() }),
  z.object({ kind: z.literal('element'), path: z.string().min(1).max(500), quote: z.string().min(1).max(2000) }),
]);
export const AddAnnotationInput = z.object({
  itemId: Id,
  revisionId: Id.optional().describe('The revision commented on; default the current one.'),
  anchor: AnchorInput.default({ kind: 'item' }),
  body: z.string().trim().min(1).max(5000),
  idempotencyKey: IdempotencyKey.optional(),
});
export const UpdateAnnotationInput = z
  .object({
    annotationId: Id,
    body: z.string().trim().min(1).max(5000).optional(),
    status: z.enum(['open', 'resolved']).optional(),
  })
  .refine((input) => input.body !== undefined || input.status !== undefined, { message: 'Nothing to change: give body and/or status.' });
export const AnnotationRefInput = z.object({ annotationId: Id });
export const ListAnnotationsInput = z.object({ itemId: Id, status: z.enum(['open', 'resolved']).optional() });

export const ReadAloudInput = z.object({
  itemId: Id,
  start: z.number().int().min(0).optional().describe('Character offset to start reading at (offsets as in read_text); default the beginning.'),
  end: z.number().int().min(1).optional().describe('Character offset to stop at; default the end of the text.'),
});

/** Change some personal settings; anything left out stays as it is. */
export const UpdatePreferencesInput = z.object({
  languages: z.array(LanguageCode).max(5).optional().describe('Languages you speak, listened for in dictation and recordings (e.g. ["en", "gu"]); [] for any.'),
  dictation: z.object({ shortcut: Shortcut.optional() }).optional(),
  readAloud: z
    .object({
      voice: z.string().min(1).max(100).nullable().optional().describe('A voice id from list_voices; null for the default voice.'),
      speed: z.number().min(0.5).max(2).optional(),
    })
    .optional(),
});

export const ChangeMemberRoleInput = z.object({ userId: Id, role: Role });
export const RemoveMemberInput = z.object({ userId: Id });
export const ConnectionRefInput = z.object({ connectionId: Id });
export const CreateConnectionTokenInput = z.object({ clientName: z.string().trim().min(1).max(100) });

export type CreateFolderInput = z.infer<typeof CreateFolderInput>;
export type ListItemsInput = z.infer<typeof ListItemsInput>;
export type UpdateItemInput = z.infer<typeof UpdateItemInput>;
export type CreateNoteInput = z.infer<typeof CreateNoteInput>;
export type EditNoteInput = z.infer<typeof EditNoteInput>;
export type AppendToNoteInput = z.infer<typeof AppendToNoteInput>;
export type ReadTextInput = z.infer<typeof ReadTextInput>;
export type BeginUploadInput = z.infer<typeof BeginUploadInput>;
export type UploadTextFileInput = z.infer<typeof UploadTextFileInput>;
export type SearchInput = z.infer<typeof SearchInput>;
export type RelatedInput = z.infer<typeof RelatedInput>;
export type ListActivityInput = z.infer<typeof ListActivityInput>;
export type ReprocessItemInput = z.infer<typeof ReprocessItemInput>;
export type TransformInput = z.infer<typeof TransformInput>;
export type InviteMemberInput = z.input<typeof InviteMemberInput>;
export type CreateShareInput = z.infer<typeof CreateShareInput>;
export type CommandInput = z.input<typeof CommandInput>;
export type UndoStep = z.infer<typeof UndoStep>;
export type UndoCommandInput = z.input<typeof UndoCommandInput>;
export type ReadAloudInput = z.infer<typeof ReadAloudInput>;
export type CreateSiteInput = z.input<typeof CreateSiteInput>;
export type ReviseSiteInput = z.infer<typeof ReviseSiteInput>;
export type AddAnnotationInput = z.input<typeof AddAnnotationInput>;
export type UpdateAnnotationInput = z.infer<typeof UpdateAnnotationInput>;
export type UpdatePreferencesInput = z.infer<typeof UpdatePreferencesInput>;
